import { useEffect, useState } from "react";
import { Form, required, useLogout, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Layout } from "@/components/supabase/layout";

import {
  FAKE_REST_MFA_TRUSTED_KEY,
  isDemoMode,
  MFA_VERIFY_PATH,
} from "./constants";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { registerTrustedDevice } from "./trustedDevice";
import { useMfaStatus } from "./useMfaStatus";

export const MfaVerifyPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const logout = useLogout();
  const { loading, factors, refresh } = useMfaStatus();
  const [submitting, setSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [factorId, setFactorId] = useState<string | null>(null);

  useEffect(() => {
    const verified = [...factors.totp, ...factors.phone].find(
      (factor) => factor.status === "verified",
    );
    if (verified) {
      setFactorId(verified.id);
    }
  }, [factors]);

  const handleDemoVerify = () => {
    if (rememberDevice) {
      localStorage.setItem(FAKE_REST_MFA_TRUSTED_KEY, "true");
    }
    navigate("/");
  };

  const handleVerify: SubmitHandler<FieldValues> = async (values) => {
    if (!factorId) {
      notify("crm.mfa.no_factor", {
        type: "error",
        messageArgs: {
          _: "No MFA factor found. Set up two-factor authentication first.",
        },
      });
      navigate("/mfa/setup");
      return;
    }

    setSubmitting(true);
    try {
      const { data: challenge, error: challengeError } =
        await getSupabaseClient().auth.mfa.challenge({ factorId });

      if (challengeError) {
        throw challengeError;
      }

      const { error: verifyError } = await getSupabaseClient().auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: values.code,
      });

      if (verifyError) {
        throw verifyError;
      }

      if (rememberDevice) {
        await registerTrustedDevice({ factorId });
      }

      await refresh();
      navigate("/");
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.verify_error", {
        type: "error",
        messageArgs: {
          _: "Invalid code. Please try again.",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <p>{translate("crm.common.loading", { _: "Loading..." })}</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("crm.mfa.verify.title", {
              _: "Verify your identity",
            })}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {translate("crm.mfa.verify.subtitle", {
              _: "Enter the code from your authenticator app or SMS.",
            })}
          </p>
        </div>

        {isDemoMode() ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
              {translate("crm.mfa.remember_device", {
                _: "Remember this device for 30 days",
              })}
            </label>
            <Button type="button" className="w-full" onClick={handleDemoVerify}>
              {translate("crm.mfa.demo.continue", { _: "Continue" })}
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleVerify}>
            <TextInput
              source="code"
              label="crm.mfa.code"
              validate={required()}
              helperText={false}
              autoComplete="one-time-code"
            />
            <label className="flex items-center gap-2 text-sm mt-4">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
              {translate("crm.mfa.remember_device", {
                _: "Remember this device for 30 days",
              })}
            </label>
            <Button
              type="submit"
              className="w-full mt-4"
              disabled={submitting || !factorId}
            >
              {translate("crm.mfa.verify.submit", { _: "Verify" })}
            </Button>
          </Form>
        )}

        <Button
          type="button"
          variant="link"
          className="w-full"
          onClick={() => logout()}
        >
          {translate("ra.auth.logout", { _: "Log out" })}
        </Button>
      </div>
    </Layout>
  );
};

MfaVerifyPage.path = MFA_VERIFY_PATH.slice(1);
